import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { LuxSpendProvider } from "./components/LuxSpend";
import { errorMessage } from "./api/client";
import { OrgProvider, useMe, useOrg } from "./api/org";
import { useAuth } from "./auth/auth";
import { AdminLayout, OrgLayout } from "./components/Layout";
import { NotFound, ValidId } from "./components/NotFound";
import { Alert, Button, Spinner } from "./components/ui";
import { AuthCallback, Login, SsoRequiredScreen } from "./pages/Login";
import { ssoRequired } from "./lib/ssoRequired";
import { useT } from "./i18n/I18nProvider";
import { INTENT_KEY, intentPath, parseCheckoutIntent, type CheckoutIntent } from "./lib/billing";
import { Onboarding } from "./pages/Onboarding";
import { GetStarted } from "./pages/GetStarted";
import { Designs } from "./pages/Designs";
import { DesignDetail } from "./pages/DesignDetail";
import { Designer } from "./pages/Designer";
import { DesignEditor } from "./editor/Editor";
import { PermitWorkspace } from "./permits/PermitWorkspace";
import { SketchPage } from "./sketch/SketchEditor";
import { Widget } from "./pages/Widget";
import { Branding } from "./pages/Branding";
import { Rules } from "./pages/Rules";
import { ElectricityPrices } from "./pages/ElectricityPrices";
import { Lux } from "./pages/Lux";
import { Team } from "./pages/Team";
import { Sso } from "./pages/Sso";
import { AdminOrgs } from "./pages/admin/Orgs";
import { AdminOrgDetail } from "./pages/admin/OrgDetail";
import { AdminUsers } from "./pages/admin/Users";
import { AdminTrials } from "./pages/admin/Trials";
import { AdminPlatformSettings } from "./pages/admin/PlatformSettings";
import { AdminPlans } from "./pages/admin/Plans";
import { AdminCoupons } from "./pages/admin/Coupons";
import { AdminStages } from "./pages/admin/Stages";
import { AdminClearDesigns } from "./pages/admin/ClearDesigns";
import { AdminCache } from "./pages/admin/Cache";
import { AdminEmails } from "./pages/admin/Emails";
import { AdminTariffDetail, AdminTariffs } from "./pages/admin/Tariffs";
import { AdminPermitEquipment } from "./pages/admin/PermitEquipment";
import { AdminPermitAhjs } from "./pages/admin/PermitAhjs";
import { ConnectWordPress } from "./pages/ConnectWordPress";
import { Integrations } from "./pages/Integrations";
import { Partners } from "./pages/Partners";
import { SolarAppConnection } from "./pages/SolarAppConnection";
import { AdminPartners } from "./pages/admin/Partners";
import { AdminMigrations } from "./pages/admin/Migrations";
import { Migration } from "./pages/Migration";
import { AdminIntegrations } from "./pages/admin/Integrations";
import { AdminAuditLog, AuditLog } from "./pages/AuditLog";
import { AcceptInvitation, ConfirmOwnership } from "./pages/Invite";
import { LibraryGroupPage, LibraryIndexPage } from "./library/LibraryPages";

function SignedIn() {
  const t = useT();
  const auth = useAuth();
  const location = useLocation();
  const me = useMe();
  if (!auth.ready) return <Spinner />;
  if (!auth.signedIn) return <Navigate to="/login" replace state={{ returnTo: location.pathname + location.search }} />;
  if (me.isLoading) return <Spinner label={t("auth.signingIn")} />;
  const refused = ssoRequired(me.error, auth.email);
  if (refused)
    return <SsoRequiredScreen email={refused.email} onRetry={() => me.refetch()} />;
  if (me.error || !me.data)
    return (
      <div className="center-fill" style={{ minHeight: "100vh", padding: 16 }}>
        <div style={{ maxWidth: 460 }} className="stack">
          <Alert tone="danger" title={t("auth.accountLoadFailed")}>
            {errorMessage(me.error)}
          </Alert>
          <div className="row">
            <Button onClick={() => me.refetch()}>{t("common.retry")}</Button>
            <Button variant="ghost" onClick={() => auth.signOut()}>
              {t("shell.signOut")}
            </Button>
          </div>
        </div>
      </div>
    );
  return (
    <OrgProvider me={me.data}>
      <LuxSpendProvider>
        <Outlet />
      </LuxSpendProvider>
    </OrgProvider>
  );
}

// The landing page's "Get Pro / Business" links (?plan=pro&billing=annual), through sign-up or sign-in: kept for the
// session, then opened on Lux & billing once the user has a company.
const landingIntent = parseCheckoutIntent(typeof window !== "undefined" ? window.location.search : "");
if (landingIntent) {
  try {
    sessionStorage.setItem(INTENT_KEY, JSON.stringify(landingIntent));
  } catch {
    /* private mode: the link still works when signed in already */
  }
}

function RequireOrg() {
  const { orgId } = useOrg();
  const location = useLocation();
  if (!orgId) return <Navigate to="/onboarding" replace />;
  let intent: CheckoutIntent | null = null;
  try {
    const raw = sessionStorage.getItem(INTENT_KEY);
    intent = raw ? (JSON.parse(raw) as CheckoutIntent) : null; // cleared by Lux & billing (BillingPanel)
  } catch {
    intent = null;
  }
  if (intent && location.pathname !== "/lux") return <Navigate to={intentPath(intent)} replace />;
  return <Outlet />;
}

function RequireAdmin() {
  const { isPlatformAdmin } = useOrg();
  if (!isPlatformAdmin) return <Navigate to="/" replace />;
  return <Outlet />;
}

function BillingRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/lux${search}`} replace />;
}

function Home() {
  const { orgId, isPlatformAdmin } = useOrg();
  if (!orgId) return <Navigate to={isPlatformAdmin ? "/admin/orgs" : "/onboarding"} replace />;
  return <Navigate to="/designs" replace />;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Login signup />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/invite/:token" element={<AcceptInvitation />} />
      <Route element={<SignedIn />}>
        <Route index element={<Home />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/connect/wordpress" element={<ConnectWordPress />} />
        <Route path="/ownership/:token" element={<ConfirmOwnership />} />
        <Route element={<RequireOrg />}>
          {/* Full screen: the design editor has no sidebar. */}
          <Route path="/designs/:designId/edit" element={<ValidId param="designId" standalone><DesignEditor /></ValidId>} />
          <Route path="/designs/:designId/permit" element={<ValidId param="designId" standalone><PermitWorkspace /></ValidId>} />
          <Route path="/designs/:designId/sketch" element={<ValidId param="designId" standalone><SketchPage /></ValidId>} />
          <Route element={<OrgLayout />}>
            <Route path="/get-started" element={<GetStarted />} />
            <Route path="/designer" element={<Designer />} />
            <Route path="/designs" element={<Designs />} />
            <Route path="/designs/:designId" element={<ValidId param="designId"><DesignDetail /></ValidId>} />
            <Route path="/widget" element={<Widget />} />
            <Route path="/branding" element={<Branding />} />
            <Route path="/rules" element={<Rules />} />
            <Route path="/electricity-prices" element={<ElectricityPrices />} />
            <Route path="/lux" element={<Lux />} />
            <Route path="/credits" element={<BillingRedirect />} />
            {/* The backend's default checkout return address (and the mock provider's). */}
            <Route path="/billing" element={<BillingRedirect />} />
            <Route path="/team" element={<Team />} />
            <Route path="/integrations" element={<Integrations />} />
            <Route path="/migrate/opensolar" element={<Migration />} />
            <Route path="/solarapp" element={<SolarAppConnection />} />
            <Route path="/financing-suppliers" element={<Partners />} />
            <Route path="/audit-log" element={<AuditLog />} />
            <Route path="/sso" element={<Sso />} />
            {/* an unknown company page: not found, in the company shell (not Designs & leads) */}
            <Route path="*" element={<NotFound />} />
          </Route>
        </Route>
        <Route element={<RequireAdmin />}>
          <Route element={<AdminLayout />}>
            <Route path="/admin" element={<Navigate to="/admin/orgs" replace />} />
            <Route path="/admin/orgs" element={<AdminOrgs />} />
            <Route path="/admin/orgs/:orgId" element={<ValidId param="orgId" admin><AdminOrgDetail /></ValidId>} />
            <Route path="/admin/audit-log" element={<AdminAuditLog />} />
            <Route path="/admin/users" element={<AdminUsers />} />
            <Route path="/admin/trials" element={<AdminTrials />} />
            <Route path="/admin/settings" element={<AdminPlatformSettings />} />
            <Route path="/admin/plans" element={<AdminPlans />} />
            <Route path="/admin/coupons" element={<AdminCoupons />} />
            <Route path="/admin/stages" element={<AdminStages />} />
            <Route path="/admin/integrations" element={<AdminIntegrations />} />
            <Route path="/admin/migrations" element={<AdminMigrations />} />
            <Route path="/admin/partners" element={<AdminPartners />} />
            <Route path="/admin/designs/clear" element={<AdminClearDesigns />} />
            <Route path="/admin/cache" element={<AdminCache />} />
            <Route path="/admin/emails" element={<AdminEmails />} />
            <Route path="/admin/tariffs" element={<AdminTariffs />} />
            <Route path="/admin/permit-equipment" element={<AdminPermitEquipment />} />
            <Route path="/admin/permit-ahjs" element={<AdminPermitAhjs />} />
            <Route path="/admin/tariffs/:tariffId" element={<AdminTariffDetail />} />
            {/* Library: internal reading pages, content served by the API to platform admins only. */}
            <Route path="/admin/library" element={<LibraryIndexPage />} />
            <Route path="/admin/library/:group" element={<LibraryGroupPage />} />
            <Route path="/admin/library/:group/:slug" element={<LibraryGroupPage />} />
            {/* an unknown admin page: not found, in the admin shell */}
            <Route path="/admin/*" element={<NotFound admin />} />
          </Route>
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
